import { apiFetch } from '@/lib/api';
import { cachedFetch, invalidate, SHELL_CACHE_TTL_MS } from '@/lib/requestCache';

import type { Vertical } from '@/services/tenantProfileService';

export type OnboardingStep =
  | 'info'
  | 'kb'
  | 'preview'
  | 'meta_oauth'
  | 'stripe_checkout'
  | 'completed';

export const MARKABLE_STEPS = [
  'info',
  'kb',
  'preview',
  'meta_oauth',
  'stripe_checkout',
] as const satisfies readonly Exclude<OnboardingStep, 'completed'>[];

export type MarkableStep = (typeof MARKABLE_STEPS)[number];

export interface OnboardingState {
  id: number;
  current_step: OnboardingStep;
  info_completed_at: string | null;
  kb_completed_at: string | null;
  preview_completed_at: string | null;
  meta_oauth_completed_at: string | null;
  stripe_completed_at: string | null;
  partial_kb_form: Record<string, unknown> | null;
  /** F127 — first-run welcome card gate. Null until the owner dismisses the
   * card (or a backfill stamped it for tenants that predate the feature). */
  welcome_seen_at: string | null;
  created_at: string;
  updated_at: string;
  /** True only when Meta App credentials are unset AND backend DEBUG=True.
   * Never true in production. Used by MetaOauthSection to render a dev stub. */
  meta_oauth_stub_available: boolean;
  /** True only when Stripe API key is unset AND backend DEBUG=True.
   * Never true in production. Used by StripeSection to render a dev stub. */
  stripe_stub_available: boolean;
}

export interface OnboardingStateUpdateRequest {
  step_completed?: MarkableStep;
  current_step?: OnboardingStep;
  partial_kb_form?: Record<string, unknown> | null;
  /** F127 — `true` stamps `welcome_seen_at` (idempotent, first-run only). */
  welcome_seen?: boolean;
}

export interface OnboardingKbSubmitRequest {
  vertical: Vertical;
  answers: Record<string, unknown>;
}

export const onboardingService = {
  /** F070: cached (30s TTL). Pass `fresh: true` where stale state breaks the
   * flow (Stripe webhook polling, post-OAuth refresh). */
  getState(opts?: { fresh?: boolean }): Promise<OnboardingState> {
    return cachedFetch('onboarding:state', () => apiFetch<OnboardingState>('/onboarding/state'), {
      ttlMs: SHELL_CACHE_TTL_MS,
      fresh: opts?.fresh,
    });
  },
  async updateState(payload: OnboardingStateUpdateRequest): Promise<OnboardingState> {
    const next = await apiFetch<OnboardingState>('/onboarding/state', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    invalidate('onboarding:state');
    return next;
  },
  /** F127 — dismiss the first-run welcome card (PATCH `welcome_seen: true`). */
  markWelcomeSeen(): Promise<OnboardingState> {
    return this.updateState({ welcome_seen: true });
  },
  async submitKb(payload: OnboardingKbSubmitRequest): Promise<OnboardingState> {
    const next = await apiFetch<OnboardingState>('/onboarding/kb', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    invalidate('onboarding:state');
    return next;
  },
};
